import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";

const faqData = [
  {
    question: "Bireysel danışmanlık süreci nasıl ilerler?",
    answer: `Danışmanlık süreci, kişinin ihtiyaçları ve başvuru nedeni doğrultusunda şekillenir. 
İlk görüşmelerde mevcut durum ve beklentiler değerlendirilir, sonrasında süreç bu çerçevede ilerletilir. 
Amaç, kişinin kendini daha iyi anlaması, duygularını düzenleyebilmesi ve yaşamında daha dengeli bir yapı kurabilmesine destek olmaktır.`,
  },
  {
    question: "Seanslar ne kadar sürer ve hangi sıklıkla yapılır?",
    answer: `Seanslar genellikle 50 dakika sürmektedir. 
Görüşmeler çoğunlukla haftada bir olacak şekilde planlanır; ancak sürecin ihtiyaçlarına göre sıklık birlikte yeniden düzenlenebilir.`,
  },
  {
    question: "Online ve yüz yüze seans seçenekleriniz nelerdir?",
    answer: `Seanslar hem online (görüntülü görüşme) hem de yüz yüze olarak gerçekleştirilebilmektedir. 
Online seanslar güvenli platformlar üzerinden yapılır. 
Yüz yüze seanslar ise uygun ve güvenli bir ortamda gerçekleşir. 
Her iki yöntemde de etik ilkeler ve gizlilik esas alınır.`,
  },
  {
    question: "Danışmanlık süreci ne kadar sürer?",
    answer: `Süreç, çalışılan konulara ve kişinin ihtiyaçlarına göre değişiklik gösterir. 
Bazı süreçler daha kısa süreli ilerlerken, bazıları daha uzun soluklu olabilir. 
Süreç düzenli olarak değerlendirilir ve gerektiğinde yeniden yapılandırılır.`,
  },
  {
    question: "İlk görüşmede neler konuşulur?",
    answer: `İlk görüşmede danışanın başvuru nedeni, beklentileri ve ihtiyaçları ele alınır. 
Aynı zamanda danışmanlık süreci hakkında genel bir bilgilendirme yapılır ve birlikte nasıl ilerlenebileceği değerlendirilir.`,
  },
  {
    question: "Seanslarda gizlilik nasıl sağlanır?",
    answer: `Danışmanlık süreci etik kurallar çerçevesinde gizlilik esasına dayalı olarak yürütülür. 
Danışanın paylaştığı bilgiler, yasal zorunluluklar dışında üçüncü kişilerle paylaşılmaz.`,
  },
  {
    question: "Seans ücretleri hakkında nasıl bilgi alabilirim?",
    answer: `Seans ücretleri; görüşme türüne (bireysel, çocuk, ergen, aile, online vb.) göre değişiklik gösterebilir.
Ücretlendirme konusunda şeffaf bilgilendirme yapılmaktadır.
Detaylı bilgi için iletişime geçebilirsiniz.`,
  },
  {
    question: "Randevu iptali veya değişikliği nasıl yapılır?",
    answer: `Randevu iptali veya değişikliği için en az 24 saat öncesinden bilgi verilmesi gerekmektedir. Bu süre içinde yapılan iptal ve değişikliklerde uygun şekilde yeni randevu planlaması yapılabilmektedir.
24 saatten daha kısa sürede yapılan iptallerde ise seans ücreti talep edilebilmektedir.`,
  },
  {
    question: "Aile danışmanlığı süreci nasıl ilerler?",
    answer: `Aile danışmanlığında ilişkisel dinamikler ele alınır ve tarafların birbirini daha iyi anlayabilmesine yönelik bir süreç yürütülür.
Seanslar, ihtiyaçlara göre bireysel veya birlikte planlanabilir.`,
  },
  {
    question: "Çocuk ve ergenlerle yapılan danışmanlık süreci nasıl yürütülür?",
    answer: `Çocuk ve ergenlerle yapılan çalışmalarda, yaş ve gelişim düzeyine uygun yöntemler kullanılır. 
Süreçte gerektiğinde ebeveynlerle iş birliği içinde ilerlenir ve çocuğun duygusal gelişimi desteklenir.`,
  },
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleIndex = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="sss" className="bg-gray-50 py-24 px-6 lg:px-12">
      {/* Başlık */}
      <motion.div
        className="max-w-4xl mx-auto text-center space-y-6"
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: false, amount: 0.3 }}
        transition={{ duration: 1.2, ease: "easeInOut" }}
      >
        <h2 className="text-4xl font-bold text-gray-900">Sıkça Sorulan Sorular</h2>
        <p className="text-gray-600 text-lg sm:text-xl max-w-2xl mx-auto">
          Danışmanlık süreciyle ilgili en sık merak edilen konuları aşağıda bulabilirsiniz.
          Daha detaylı bilgi için bizimle iletişime geçebilirsiniz.
        </p>
      </motion.div>

      {/* Accordion */}
      <div className="mt-16 max-w-3xl mx-auto space-y-4">
        {faqData.map((faq, index) => (
          <motion.div
            key={index}
            className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-200"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: false, amount: 0.3 }}
            transition={{ duration: 0.8, delay: index * 0.1, ease: "easeInOut" }}
          >
            <button
              onClick={() => toggleIndex(index)}
              className="w-full flex justify-between items-center p-6 text-left focus:outline-none"
            >
              <span className="font-semibold text-gray-900 text-lg">{faq.question}</span>
              {openIndex === index ? (
                <ChevronUpIcon className="h-6 w-6 text-pink-600" />
              ) : (
                <ChevronDownIcon className="h-6 w-6 text-pink-600" />
              )}
            </button>

            <AnimatePresence>
              {openIndex === index && (
                <motion.div
                  key="content"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.6, ease: "easeInOut" }}
                  className="px-6 pb-6 text-gray-700 leading-relaxed"
                >
                  {faq.answer.split("\n").map((line, i) => (
                    <p key={i} className="mb-2">{line}</p>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
